import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { App } from "./App";
import { queryClient } from "./lib/query-client";
import { takeOAuthReturn } from "./lib/oauth-popup";
import { initPostHog } from "./lib/posthog";
import { watchRenderedErrors } from "./lib/error-analytics";
import { initDiagCapture } from "./lib/diag-capture";
import { initDashboardSessionMarker } from "./lib/dashboard-session";
import { UnhandledFailureNotice } from "./components/UnhandledFailureNotice";
import "./styles.css";
// Token layer for the ported prototype surfaces. Loaded once, globally, because
// several surfaces consume it; see the file header.
import "./styles/prototype-tokens.css";
// Shared chrome for those surfaces. Scoped under .fx-page, so it is inert
// until a page opts in by rendering that class.
import "./styles/prototype-pages.css";

// If this page was opened as an OAuth popup, close it immediately so the
// parent window's polling loop picks up the new connection. The /oauth-done
// pathname check matters for callbacks that append no oauth_* marker (UCL owns
// the callback for migrated orgs and redirects to the bare returnUrl) — the
// SPA route handles those too, but closing here beats loading the app first.
let closeRequested = false;
let closeFailure: string | null = null;
const leavingThisPage = ((): boolean => {
  const params = new URLSearchParams(window.location.search);
  const isOAuthReturn =
    params.has("oauth_success") || params.has("oauth_error") || /\/oauth-done\/?$/.test(window.location.pathname);
  // NOT gated on window.opener. COOP severs it: app.fastn.dev serves
  // Cross-Origin-Opener-Policy: same-origin-allow-popups, and a popup coming
  // back from a cross-origin consent page onto such a document gets a fresh
  // browsing context group, so `opener` is null in exactly the case this code
  // exists to handle. Gating the close on it left the popup loading the whole
  // dashboard inside itself.
  //
  // The breadcrumb is what distinguishes a popup from the user's OWN tab
  // mid-redirect (the blocked-popup fallback). Only that tab has one: it is
  // written immediately before navigating away, and a popup's sessionStorage
  // was copied when it opened, which is strictly earlier.
  // That argument holds for the CURRENT flow and not for a previous one: a popup inherits a COPY
  // of its opener's session storage at open time, so an abandoned same-tab redirect used to leave
  // a breadcrumb that every later popup inherited, inverting this decision and sending the popup
  // into the app instead of closing it. openOAuthPopup now clears it before the popup exists.
  const back = isOAuthReturn ? takeOAuthReturn() : null;
  if (isOAuthReturn && back) {
    // The user's own tab. Send it back HERE, before ReactDOM.createRoot — not
    // from a route effect. AuthProvider calls keycloak.init({ onLoad:
    // "login-required" }) on mount, which starts its own full-page redirect to
    // Keycloak; that supersedes a navigation issued from an effect, and
    // Keycloak then returns the tab to the URL it left — /oauth-done — with
    // the breadcrumb already consumed. The user lands stranded on a
    // "you can close this window" page in their only tab.
    window.location.replace(back);
    return true;
  }
  if (isOAuthReturn) {
    // Forward the failure. The callback reports every early exit - expired flow, missing auth
    // method, "OAuth configuration incomplete" - as `oauth_error` on this URL, and ConnectModal
    // already knows what to do with `e.data.error`: show it and keep the dialog open. Posting the
    // bare message threw that away, so a failed connect was indistinguishable from a successful
    // one: the popup closed itself, the modal closed with it, and no connection ever appeared.
    const failure = params.get("oauth_error");
    closeFailure = failure;
    try {
      (window.opener as Window | null)?.postMessage(
        { type: "fastn:oauth-done", ...(failure ? { error: failure } : {}) },
        "*",
      );
    } catch { /* opener gone */ }
    // `window.close()` is BEST EFFORT and returns immediately either way. Falling through to the
    // mount on a refused close is what put the whole dashboard inside the popup: the app boots,
    // AuthProvider's keycloak.init({ onLoad: "login-required" }) starts a full-page redirect, and
    // the popup ends up parked on a dashboard route instead of a page saying it can be closed.
    //
    // A refusal is the NORMAL case here, not an edge one. The close is requested precisely when
    // the opener was severed - app.*.gcp.fastn.ai serves Cross-Origin-Opener-Policy:
    // same-origin-allow-popups, so a popup returning from a cross-origin consent screen lands in a
    // fresh browsing context group - and a window the browser no longer regards as script-opened
    // is one it may decline to close.
    //
    // So this window is leaving whether or not the close lands, and nothing below may mount.
    window.close();
    closeRequested = true;
    return true;
  }
  return false;
})();

// A close that was refused leaves this window on screen. Paint the same line OAuthDonePage would
// rather than a blank document - without React, and without AuthProvider, whose Keycloak redirect
// is the thing that navigated the popup away from here in the first place.
if (closeRequested) {
  const root = document.getElementById("root");
  if (root) {
    root.style.cssText =
      "display:flex;align-items:center;justify-content:center;height:100vh;font-family:system-ui;font-size:14px;padding:40px;text-align:center";
    // THIS is the window that has to carry a failure. The close is requested exactly when the
    // opener was severed, so there is no postMessage either - nothing else is left to tell. An
    // earlier revision hardcoded "Connected." here and put the failure handling in
    // `OAuthDonePage`, which cannot run: `isOAuthReturn` matches /oauth-done, so this bootstrap
    // always leaves and React never mounts.
    if (closeFailure) {
      // Immediately: the window is staying, and there is nothing to wait for.
      root.textContent = `${closeFailure} You can close this window.`;
    } else {
      // A successful close usually lands within a frame or two. Painting at once would flash this
      // line on every working connect, so say nothing until the close has visibly not happened.
      window.setTimeout(() => {
        root.textContent = "Connected. You can close this window.";
      }, 500);
    }
  }
}

// Nothing below this point may run while a navigation is already committed:
// mounting the app is what starts AuthProvider's Keycloak redirect, and that
// redirect is what superseded the return navigation.
if (!leavingThisPage) {

// Initialize PostHog product analytics. Autocapture covers pageviews and
// clicks; identifyUser() runs later from AuthProvider. No-ops if
// VITE_POSTHOG_KEY is unset.
initPostHog();
// Every error alert the page renders, reported with its text (error_shown).
watchRenderedErrors();

// Before anything can issue an API request: an unmarked request is treated as
// an API session and lands in the user's home org, not the org on screen.
initDashboardSessionMarker();

// Initialize the diagnostic-capture buffer (fetch / XHR / console.error+warn)
// so the bug-report widget can include the most recent activity.
initDiagCapture();

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      <UnhandledFailureNotice />
      {import.meta.env.DEV && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
      )}
    </QueryClientProvider>
  </React.StrictMode>,
);

}
